import React, { useMemo, useState } from 'react'
import { MagnifyingGlass, PaperPlaneTilt, WarningCircle, X } from '@phosphor-icons/react'
import {
  Button,
  CheckboxV2,
  Dialog,
  DialogContent,
  DialogOverlay,
  Icon,
  IconButton,
  Input
} from '@shopflo/ui'
import { useLocale } from 'src/lib/hooks/useLocal'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import { postRequest } from 'src/lib/core/api-client'
import { apiURI } from 'src/lib/utils/constants'
import { useUser } from 'src/lib/contexts/UserContext'
import { useReports } from 'src/contexts/ReportContext'

interface ShareModalProps {
  open: boolean
  onClose: () => void
  /** A single run, or several runs when sharing a bulk selection from Run History. */
  runId: string | string[]
}

// Teal avatar with the recipient's leading initial.
const RecipientAvatar: React.FC<{ email: string }> = ({ email }) => (
  <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[rgba(0,150,137,0.12)] text-text-sm-semibold uppercase tracking-wide text-[#009689]'>
    {email?.charAt(0) ?? '?'}
  </span>
)

export default function ShareModal({ open, onClose, runId }: ShareModalProps) {
  const { t } = useLocale()
  const { user } = useUser()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedEmails, setSelectedEmails] = useState<string[]>(user?.email ? [user.email] : [])
  const [search, setSearch] = useState('')
  const { accounts, isValidating, emailError } = useReports()

  const selected = useMemo(() => new Set(selectedEmails), [selectedEmails])

  const query = search.trim().toLowerCase()
  const visibleAccounts = useMemo(
    () => (query ? accounts.filter(account => account.email.toLowerCase().includes(query)) : accounts),
    [accounts, query]
  )

  const allSelected =
    visibleAccounts.length > 0 && visibleAccounts.every(account => selected.has(account.email))
  const someSelected = selected.size > 0

  const toggleEmail = (email: string) =>
    setSelectedEmails(prev => (prev.includes(email) ? prev.filter(item => item !== email) : [...prev, email]))

  const selectAll = () =>
    setSelectedEmails(prev => Array.from(new Set([...prev, ...visibleAccounts.map(account => account.email)])))
  const clearAll = () =>
    setSelectedEmails(prev => prev.filter(email => !visibleAccounts.some(account => account.email === email)))

  const handleSubmit = async () => {
    if (selectedEmails.length === 0) return
    const runIds = Array.isArray(runId) ? runId : [runId]
    if (runIds.length === 0) return
    try {
      setIsSubmitting(true)
      // The share endpoint accepts an array of runIds, so a single request covers one or many runs.
      await postRequest('/dashboard/report/share', apiURI.CHECKOUT_NODE, { runId: runIds, emails: selectedEmails })
      successToast(t('report.share.success'))
      onClose()
    } catch (error) {
      errorToast(t('report.share.failed'))
      console.error(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={value => !value && onClose()}>
      <DialogOverlay className='fixed inset-0 z-40 bg-black/40' />
      <DialogContent className='z-50 flex w-[600px] max-w-[92vw] flex-col gap-0 overflow-hidden !rounded-2xl bg-white p-0 shadow-modal'>
        {/* Header */}
        <div className='flex items-center gap-3 px-5 pt-4'>
          <span className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-zinc-100'>
            <Icon IconComponent={PaperPlaneTilt} size='lg' className='text-zinc-600' iconProps={{ weight: 'duotone' }} />
          </span>
          <span className='flex-1 text-text-md-semibold text-zinc-900'>Send</span>
          <IconButton IconComponent={X} size='sm' variant='ghost' onClick={onClose} />
        </div>

        {/* Body */}
        <div className='flex flex-col gap-4 pb-5 pt-4'>
          {emailError ? (
            <div className='mx-5 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2'>
              <Icon IconComponent={WarningCircle} size='sm' className='shrink-0 text-amber-600' />
              <p className='text-text-sm-regular text-zinc-700'>
                You don&apos;t have access to download reports. Ask your store admin for permission.
              </p>
            </div>
          ) : (
            <>
              {/* Search */}
              <div className='px-5'>
                <Input
                  type='text'
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder='Search people'
                  variant='filled'
                  dimension='lg'
                  startAdornment={<Icon IconComponent={MagnifyingGlass} size='sm' className='shrink-0 text-zinc-500' />}
                />
              </div>

              {/* Recipient list */}
              <div className='flex h-[397px] flex-col overflow-y-auto overscroll-contain'>
                {isValidating && accounts.length === 0 ? (
                  Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className='mx-5 h-10 animate-pulse rounded-lg bg-zinc-100' />
                  ))
                ) : (
                  <>
                    {/* Select-all header — sticky at the top of the scrolling list. */}
                    <div className='sticky top-0 z-10 flex items-center gap-3 bg-white mx-5 px-0.5 py-1.5'>
                      <span className='flex w-6 shrink-0 justify-center'>
                        <CheckboxV2
                          checked={allSelected}
                          indeterminate={!allSelected && someSelected}
                          size='sm'
                          onCheckedChange={() => (allSelected ? clearAll() : selectAll())}
                        />
                      </span>
                      <span className='flex-1 text-text-sm-medium text-zinc-500'>{accounts.length} users</span>
                    </div>

                    {visibleAccounts.length === 0 ? (
                      <p className='px-5 py-8 text-center text-text-sm-regular text-zinc-400'>No people found</p>
                    ) : (
                      visibleAccounts.map(account => (
                        <button
                          key={account.email}
                          type='button'
                          onClick={() => toggleEmail(account.email)}
                          className='mx-4 flex items-center gap-2 rounded-lg px-1.5 py-1 text-left transition-colors hover:bg-zinc-50'
                        >
                          <span className='flex w-6 shrink-0 justify-center'>
                            <CheckboxV2 checked={selected.has(account.email)} size='sm' tabIndex={-1} aria-hidden />
                          </span>
                          <RecipientAvatar email={account.email} />
                          <span className='flex min-w-0 flex-1 flex-col'>
                            <span className='truncate text-text-sm-medium text-zinc-900'>{account.email}</span>
                            {account.role && (
                              <span className='truncate text-text-xs-regular text-zinc-500'>{account.role}</span>
                            )}
                          </span>
                        </button>
                      ))
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className='flex items-center justify-between px-5 pb-4'>
          <span className='text-text-sm-regular text-zinc-500'>{selectedEmails.length} selected</span>
          <Button
            variant='primary'
            size='md'
            loading={isSubmitting}
            disabled={!selectedEmails.length || !!emailError || isSubmitting}
            onClick={handleSubmit}
          >
            Send
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
